import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useT } from "@/contexts/LanguageContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Truck, Clock, Users, ArrowRight, Send, ShieldCheck, Globe, MapPin, X, AlertTriangle, Droplets, Route, Shield, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/85c91bfc-9293-4298-9c5b-f18866d778c2/images/41333581af1056453c90e1f3953c6f4ed44795836cd596933778904231393350.jpeg";
const TEAM_IMG = "https://static.prod-images.emergentagent.com/jobs/85c91bfc-9293-4298-9c5b-f18866d778c2/images/5074449ceec7a0ca1dd00c5b9f1248d14d6eb46dbc76abb424d1187f9a5c5305.png";

function AnimatedCounter({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const num = parseInt(target.replace(/\D/g, ""));
          const step = Math.ceil(num / (duration / 16));
          let current = 0;
          const timer = setInterval(() => {
            current += step;
            if (current >= num) {
              current = num;
              clearInterval(timer);
            }
            setCount(current);
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  const display = target.includes(".") ? count.toLocaleString("de-DE") : count.toString();
  return <span ref={ref}>{display}{suffix}</span>;
}

function ScrollReveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const t = useT();
  const { lang } = useLanguage();
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [showJobAd, setShowJobAd] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("jobAdDismissed");
    if (!dismissed) setShowJobAd(true);
  }, []);

  const dismissJobAd = () => {
    setShowJobAd(false);
    sessionStorage.setItem("jobAdDismissed", "true");
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error(t("contact_validation"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success(t("contact_success"));
      setContactForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t("contact_error"));
    } finally {
      setSending(false);
    }
  };

  const isDE = lang === "de";

  return (
    <div className="min-h-screen" data-testid="homepage">
      <Navbar />

      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center pt-20">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="AtoB Logistics" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2.5 mb-8 bg-white/10 backdrop-blur-sm px-4 py-2.5 rounded-sm w-fit" data-testid="holding-badge">
              <span className="text-xs text-white/80 font-medium">{t("hero_holding")}</span>
              <img src="/basic-united-logo.png" alt="Basic United Holding GmbH" className="h-7 w-auto brightness-0 invert opacity-90" />
            </div>
            <div className="inline-block mb-6">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFD700] bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-sm">
                {t("hero_badge")}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white mb-6 leading-[1.05]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="hero-heading">
              {t("hero_heading")}
            </h1>
            <p className="text-base sm:text-lg text-white/80 mb-8 max-w-lg leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="#kontakt" data-testid="hero-cta-contact">
                <Button className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90 font-semibold px-8 py-3 h-auto rounded-sm text-sm">
                  {t("hero_cta_contact")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#leistungen" data-testid="hero-cta-services">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white font-medium px-8 py-3 h-auto rounded-sm text-sm">
                  {t("services_overline")}
                </Button>
              </a>
            </div>
          </div>
        </div>
        {/* Scrolling ticker */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#002FA7]/95 backdrop-blur-sm overflow-hidden">
          <div className="animate-ticker flex whitespace-nowrap py-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-8 mx-8 text-sm text-white/80 font-medium shrink-0">
                <span className="flex items-center gap-2"><Route className="w-4 h-4 text-[#FFD700]" />{isDE ? "Europaweite Transporte" : "Europe-wide transport"}</span>
                <span className="text-[#FFD700]">/</span>
                <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FFD700]" />{isDE ? "ADR-Gefahrguttransporte" : "ADR hazardous goods transport"}</span>
                <span className="text-[#FFD700]">/</span>
                <span className="flex items-center gap-2"><Droplets className="w-4 h-4 text-[#FFD700]" />{isDE ? "Tanklogistik & Flüssigtransporte" : "Tank logistics & liquid transport"}</span>
                <span className="text-[#FFD700]">/</span>
                <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#FFD700]" />{isDE ? "ADR-zertifizierte Fahrer" : "ADR-certified drivers"}</span>
                <span className="text-[#FFD700]">/</span>
                <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#FFD700]" />{isDE ? "Komplettladungen & Stückgut" : "Full loads & part loads"}</span>
                <span className="text-[#FFD700]">/</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[#002FA7] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Truck, label: isDE ? "Industrie & Handel" : "Industry & Commerce" },
              { icon: ShieldCheck, label: isDE ? "Zertifiziert & versichert" : "Certified & insured" },
              { icon: Globe, label: isDE ? "Europaweite Abdeckung" : "Europe-wide coverage" },
              { icon: Clock, label: isDE ? "Termintreue garantiert" : "On-time guarantee" },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <item.icon className="w-5 h-5 text-[#FFD700]" />
                <div className="text-sm font-medium text-white/90">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section id="leistungen" className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7]">{t("services_overline")}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#0A0A0A] mt-2 mb-14" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t("services_heading")}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Truck, title: t("service_1_title"), desc: t("service_1_desc") },
              { icon: Clock, title: t("service_2_title"), desc: t("service_2_desc") },
              { icon: Users, title: t("service_3_title"), desc: t("service_3_desc") },
            ].map((service, i) => (
              <ScrollReveal key={service.title} delay={i * 120}>
                <div className="group border border-border p-6 h-full hover:-translate-y-1 hover:shadow-lg hover:shadow-[#002FA7]/5 transition-all duration-300">
                  <div className="w-10 h-10 bg-[#002FA7] rounded-sm flex items-center justify-center mb-4 group-hover:bg-[#FFD700] transition-colors duration-300">
                    <service.icon className="w-5 h-5 text-[#FFD700] group-hover:text-[#002FA7] transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{service.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{service.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ADR & Tanklogistik Expertise */}
      <section className="py-20 bg-[#0A0A0A] text-white" data-testid="adr-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollReveal>
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFD700]">
                  {isDE ? "Spezialtransporte" : "Specialist Transport"}
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white mt-2 mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {isDE ? "ADR-Gefahrgut & Tanklogistik" : "ADR Hazardous Goods & Tank Logistics"}
                </h2>
                <p className="text-base text-gray-400 leading-relaxed mb-8">
                  {isDE
                    ? "Als zertifizierter Gefahrgut-Spediteur transportieren wir gefährliche Güter aller ADR-Klassen sicher und rechtskonform durch ganz Europa. Unsere Tanklogistik umfasst den Transport von Flüssigkeiten, Chemikalien und Gefahrstoffen in Tankcontainern und Tankaufliegern."
                    : "As a certified hazardous goods carrier, we transport dangerous goods of all ADR classes safely and compliantly across Europe. Our tank logistics cover the transport of liquids, chemicals and hazardous materials in tank containers and tank trailers."}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: AlertTriangle, text: isDE ? "Alle ADR-Gefahrgutklassen 1–9" : "All ADR hazard classes 1–9" },
                    { icon: Droplets, text: isDE ? "Tankcontainer & Tankauflieger" : "Tank containers & tank trailers" },
                    { icon: Shield, text: isDE ? "ADR-zertifizierte Berufskraftfahrer" : "ADR-certified professional drivers" },
                    { icon: Globe, text: isDE ? "Europaweit mit Gefahrgutbeauftragtem" : "Europe-wide with DGSA on staff" },
                    { icon: Route, text: isDE ? "Gefahrgut-Routenplanung" : "Hazmat route planning" },
                    { icon: ShieldCheck, text: isDE ? "SQAS-konforme Prozesse" : "SQAS-compliant processes" },
                  ].map((item, i) => (
                    <ScrollReveal key={item.text} delay={i * 80}>
                      <div className="flex items-start gap-3 group">
                        <div className="w-8 h-8 bg-[#FFD700]/10 rounded-sm flex items-center justify-center shrink-0 group-hover:bg-[#FFD700]/20 transition-colors">
                          <item.icon className="w-4 h-4 text-[#FFD700]" />
                        </div>
                        <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{item.text}</span>
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: isDE ? "ADR-Klassen" : "ADR Classes", value: "1–9", sub: isDE ? "Alle Gefahrgutklassen" : "All hazard classes" },
                  { label: isDE ? "Tankkapazität" : "Tank Capacity", value: "33.000L", sub: isDE ? "Pro Tankauflieger" : "Per tank trailer" },
                  { label: isDE ? "Kompetenz" : "Expertise", value: "ADR", sub: isDE ? "Alle Gefahrgutklassen" : "All hazard classes" },
                  { label: isDE ? "Einsatzgebiet" : "Coverage", value: "EU", sub: isDE ? "Europaweit" : "Europe-wide" },
                ].map((card) => (
                  <div key={card.label} className="border border-gray-800 p-5 hover:border-[#FFD700]/30 transition-colors group">
                    <div className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">{card.label}</div>
                    <div className="text-3xl font-black text-[#FFD700] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{card.value}</div>
                    <div className="text-xs text-gray-500">{card.sub}</div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Geschäftsführer & Vision */}
      <section className="py-20 bg-white" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <ScrollReveal className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full bg-[#002FA7]" />
                <img
                  src="/admir-basic.webp"
                  alt="Admir Basic – Geschäftsführer AtoB Logistic Solutions GmbH"
                  className="relative z-10 w-full h-auto object-cover"
                />
                <div className="absolute -bottom-4 -right-4 z-20 bg-[#FFD700] px-6 py-4">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7]">
                    {isDE ? "Geschäftsführer" : "Managing Director"}
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200} className="lg:col-span-7">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7]">
                  {isDE ? "Die Führung" : "Leadership"}
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#0A0A0A] mt-2 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Admir Basic
                </h2>
                <p className="text-base text-[#002FA7] font-medium mb-6">
                  {isDE ? "Gründer & Geschäftsführer" : "Founder & Managing Director"}
                </p>

                <blockquote className="border-l-4 border-[#FFD700] pl-5 mb-8">
                  <p className="text-lg text-[#0A0A0A] font-medium italic leading-relaxed" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {isDE
                      ? "\u201EWir sehen keine Probleme \u2013 nur Aufgaben, f\u00FCr die es eine L\u00F6sung gibt.\u201C"
                      : '"We don\'t see problems \u2013 only challenges that have a solution."'}
                  </p>
                </blockquote>

                <p className="text-sm text-[#475569] leading-relaxed mb-6">
                  {isDE
                    ? "Als junger Unternehmer mit einem klaren Blick für das Wesentliche hat Admir Basic AtoB Logistic Solutions gegründet – mit einer Mission: Logistik neu denken. Seine Hands-on-Mentalität prägt das gesamte Unternehmen. Wo andere Hindernisse sehen, findet er Wege. Wo andere zögern, handelt er."
                    : "As a young entrepreneur with a clear focus on what matters, Admir Basic founded AtoB Logistic Solutions with one mission: rethinking logistics. His hands-on mentality shapes the entire company. Where others see obstacles, he finds solutions. Where others hesitate, he acts."}
                </p>
                <p className="text-sm text-[#475569] leading-relaxed mb-8">
                  {isDE
                    ? "An seiner Seite steht ein erfahrenes Team aus Logistik-Profis, Gefahrgut-Spezialisten und Disponenten mit umfassender Branchenerfahrung. Diese Kombination aus frischem Unternehmergeist und geballter Expertise macht AtoB zu einem Partner, auf den sich Industrie und Handel verlassen k\u00F6nnen \u2013 bei jedem Transport, in jeder Situation."
                    : "By his side is an experienced team of logistics professionals, hazardous goods specialists and dispatchers with extensive industry experience. This combination of fresh entrepreneurial spirit and concentrated expertise makes AtoB a partner that industry and commerce can rely on \u2013 for every transport, in every situation."}
                </p>

                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: isDE ? "Lösungsorientiert" : "Solution-driven", sub: isDE ? "Keine Probleme, nur Lösungen" : "No problems, only solutions" },
                    { value: "Hands-on", sub: isDE ? "Direkt. Persönlich. Engagiert." : "Direct. Personal. Committed." },
                    { value: isDE ? "Erfahrenes Team" : "Expert Team", sub: isDE ? "Geballte Branchenexpertise" : "Combined industry expertise" },
                  ].map((item) => (
                    <div key={item.value} className="border-t-2 border-[#002FA7] pt-3">
                      <div className="text-sm font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{item.value}</div>
                      <div className="text-xs text-[#475569] mt-1">{item.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Team & Unternehmen */}
      <section className="py-20 bg-[#F3F5F7]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7]">{t("about_overline")}</span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#0A0A0A] mt-2 mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {t("about_heading")}
                </h2>
                <p className="text-sm text-[#475569] leading-relaxed mb-6">{t("about_text")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: Globe, textKey: "about_feat_1" },
                    { icon: ShieldCheck, textKey: "about_feat_2" },
                    { icon: Clock, textKey: "about_feat_3" },
                    { icon: Truck, textKey: "about_feat_4" },
                  ].map((item) => (
                    <div key={item.textKey} className="flex items-center gap-3 text-sm text-[#0A0A0A]">
                      <item.icon className="w-4 h-4 text-[#002FA7] shrink-0" />
                      {t(item.textKey)}
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className="relative">
                <img src={TEAM_IMG} alt="Team" className="w-full h-[400px] object-cover" />
                <div className="absolute -bottom-4 -left-4 bg-[#002FA7] text-white p-6 max-w-[220px]">
                  <div className="text-sm font-bold text-[#FFD700] uppercase tracking-wide" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{isDE ? "Ihr Partner" : "Your Partner"}</div>
                  <div className="text-xs text-white/70 mt-1">{isDE ? "für Industrie & Handel" : "for industry & commerce"}</div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-20 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ScrollReveal>
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7]">{t("contact_overline")}</span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#0A0A0A] mt-2 mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {t("contact_heading")}
                </h2>
                <p className="text-sm text-[#475569] leading-relaxed mb-8">{t("contact_text")}</p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#002FA7]/10 rounded-sm flex items-center justify-center shrink-0">
                      <Phone className="w-4 h-4 text-[#002FA7]" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#0A0A0A]">{isDE ? "Telefon" : "Phone"}</div>
                      <a href="tel:+4921732046451" className="text-sm text-[#475569] hover:text-[#002FA7] transition-colors">+49 2173 2046451</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#002FA7]/10 rounded-sm flex items-center justify-center shrink-0">
                      <Send className="w-4 h-4 text-[#002FA7]" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#0A0A0A]">{t("contact_email_label")}</div>
                      <a href="mailto:info@atob-logistics.de" className="text-sm text-[#475569] hover:text-[#002FA7] transition-colors">info@atob-logistics.de</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#002FA7]/10 rounded-sm flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-[#002FA7]" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#0A0A0A]">{t("contact_address_label")}</div>
                      <span className="text-sm text-[#475569]">Schneiderstraße 63, 40764 Langenfeld (Rheinland)</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <form onSubmit={handleContact} className="space-y-4 border border-border p-6" data-testid="contact-form">
                <div>
                  <label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569] mb-1.5 block">{t("contact_name")}</label>
                  <Input data-testid="contact-name-input" placeholder={t("contact_name_placeholder")} value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569] mb-1.5 block">{t("contact_email")}</label>
                  <Input data-testid="contact-email-input" type="email" placeholder={t("contact_email_placeholder")} value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569] mb-1.5 block">{t("contact_message")}</label>
                  <Textarea data-testid="contact-message-input" placeholder={t("contact_message_placeholder")} rows={5} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} className="rounded-sm resize-none" />
                </div>
                <Button type="submit" disabled={sending} className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white font-semibold rounded-sm h-11" data-testid="contact-submit-button">
                  {sending ? t("contact_sending") : t("contact_submit")}
                </Button>
              </form>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
